<template>
  <div
    id="vessel-position-history-timeline-controller"
    class="h-full flex flex-col"
  >
    <vs-row>
      <ul id="timeline-mode-label">
        <li>再生時間</li>
        <li class="time-label">{{ timeLabel }}</li>
      </ul>
    </vs-row>
    <vs-row
      vs-type="flex"
      class="justify-center items-center h-full"
    >
      <button
        class="timeline-control-button cursor-pointer mr-auto ml-4"
        @click="onClickPrev"
        :disabled="isSliderMin"
      >
        <feather-icon
          icon="ArrowLeftCircleIcon"
        />
      </button>
      <vue-slider
        v-if="enableTime"
        id="timeline-slider"
        v-model="sliderValue"
        :marks="sliderMarks"
        :max="144"
        :fixed="true"
        :tooltip-formatter="formatter"
        :tooltip-placement="'bottom'"
        @drag-start="onDragStart"
        @dragging="onDragging"
        @drag-end="onDragEnd"
        @change="onChange"
      >
        <template v-slot:dot>
          <div class="custom-dot"/>
        </template>
      </vue-slider>
      <button
        class="timeline-control-button cursor-pointer ml-auto mr-4"
        @click="onClickNext"
        :disabled="isSliderMax"
      >
        <feather-icon
          icon="ArrowRightCircleIcon"
        />
      </button>
    </vs-row>
  </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/antd.css";
import { createNamespacedHelpers } from "vuex";
import { DateUtil } from "@/utils";

// components
import "flatpickr/dist/flatpickr.css";

// Store
const { mapActions: mapActionsOfVesselView } = createNamespacedHelpers(
  "enterpriseVesselView"
);

export default {
  components: {
    VueSlider,
  },
  props: {
    vesselId: {
      type: String,
      required: true
    },
    startTimeUtc: {
      type: String,
      required: true
    },
    endTimeUtc: {
      type: String,
      required: true
    },
    enableTime: {
      type: Object,
      required: true
    },
    timeLabel: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      show: false,
      currentHistoryDay: null,
      currentSliderValue: null,
      isDragging: false,
      radioColor: "#5097D5",
      sliderDisabled: true,
      sliderValue: [0, 1],
      preSliderValue: null,
      sliderMarks: null,
      historyDate: null,
      dayLocal: null,
      formatter: _ => `${this.toolTipFormat()}`,

      customStyle: {height: "100px"}
    };
  },
  watch: {
    startTimeUtc: {
      immediate: true,
      handler: async function() {
        this.dayLocal = DateUtil.toMomentObj(this.startTimeLocal).format(
          "YYYY-MM-DD"
        );
      }
    }
  },
  computed: {
    isSliderMin() {
      return this.sliderValue[0] <= 0;
    },
    isSliderMax() {
      return this.sliderValue[1] >= 144;
    },
    startTimeLocal() {
      return DateUtil.toMomentObj(this.startTimeUtc).local().format();
    },
    endTimeLocal() {
      return DateUtil.toMomentObj(this.endTimeUtc).local().format();
    },
  },
  methods: {
    ...mapActionsOfVesselView(["setVoyageHistoryDateTime", "setMode"]),

    getMarksLabelColor(val) {
      let target = `${this.dayLocal}T${val}:00`;
      let fromLocal = this.enableTime.fromLocal.split(".")[0];
      let toLocal = this.enableTime.toLocal.split(".")[0];
      return DateUtil.isBetween(target, fromLocal, toLocal)
        ? "#333333"
        : "#e3e3e3";
    },

    crateSliderMarks10min() {
      let style = {};
      let count = 0;
      let count2 = 1;
      for (let i = 0; i < 145; i++) {
        if (i % 6 === 0) {
          let hh;
          let h;
          if (count <= 9) {
            hh = "0" + String(count).slice(-2);
            h = String(count);
          } else {
            hh = String(count);
            h = String(count);
          }
          style[i] = {
            label: `${h}:00`,
            labelStyle: {
              color: this.getMarksLabelColor(`${hh}:00`)
            },
            sliderValue: [i, i + 6],
            timeValue: `${hh}:00`,
            time: `${hh}:00`
          };
          count++;
        } else {
          let hh;
          if (count - 1 <= 9) {
            hh = "0" + String(count - 1).slice(-2);
          } else {
            hh = String(count - 1);
          }
          let mm = `${count2}0`;
          style[i] = {
            label: ``,
            sliderValue: [i, i + 6],
            timeValue: `${hh}:${mm}`,
            time: `${hh}:${mm}`
          };
          if (count2 === 5) {
            count2 = 1;
          } else {
            count2++;
          }
        }
      }
      this.sliderMarks = style;
    },

    async onChange() {
      if (!this.isDragging && this.preSliderValue !== this.sliderValue) {
        let target = `${this.dayLocal}T${this.sliderValueToTime10min(
          this.sliderValue[0]
        )}:00`;
        let fromLocal = this.enableTime.fromLocal.split(".")[0];
        let toLocal = this.enableTime.toLocal.split(".")[0];
        if (DateUtil.isBetween(target, fromLocal, toLocal)) {
          let self = this;
          setTimeout(function() {
            self.updateVoyageHistoryDateTime();
          }, 1000);
          this.preSliderValue = this.sliderValue;
        } else {
          setTimeout(() => {
            this.sliderValue = this.preSliderValue;
          }, 200);
        }
      }
    },
    async onClickPrev() {
      const sliderValueStart = this.sliderValue[0] - 6 >= 0 ? this.sliderValue[0] - 6 : 0;
      this.sliderValue = [
        sliderValueStart,
        sliderValueStart + 6
      ];
      await this.onChange();
    },
    async onClickNext() {
      const sliderValueEnd = this.sliderValue[1] + 6 <= 144 ? this.sliderValue[1] + 6 : 144;
      this.sliderValue = [
        sliderValueEnd - 6,
        sliderValueEnd
      ];
      await this.onChange();
    },

    onDragStart() {
      this.isDragging = true;
    },

    onDragging() {
      this.isDragging = true;
    },

    async onDragEnd() {
      this.isDragging = false;
      if (this.preSliderValue[0] === this.sliderValue[0]) {
        return;
      }
      let target = DateUtil.toMomentObj(
        `${this.dayLocal}T${this.sliderValueToTime10min(this.sliderValue[0])}:00`
      );
      let fromLocal = DateUtil.toMomentObj(
        this.enableTime.fromLocal.split(".")[0]
      );
      let toLocal = DateUtil.toMomentObj(this.enableTime.toLocal.split(".")[0]);
      if (DateUtil.isBetween(target, fromLocal, toLocal)) {
        await this.updateVoyageHistoryDateTime();
        this.preSliderValue = this.sliderValue;
      } else {
        this.sliderValue = this.preSliderValue;
      }
    },

    async updateVoyageHistoryDateTime() {
      await this.setVoyageHistoryDateTime(null);
      let dayLocal = DateUtil.toMomentObj(this.startTimeUtc).format(
        "YYYY-MM-DD"
      );
      let historyStartTime = this.sliderValueToTime10min(this.sliderValue[0]);
      let startTimeLocal = `${dayLocal}T${historyStartTime}:00`;
      let startTimeUtc = DateUtil.toMomentObj(startTimeLocal)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss");
      let endTimeUtc = DateUtil.addTime(startTimeUtc, 60, "m").format(
        "YYYY-MM-DDTHH:mm:ss"
      );
      await this.setVoyageHistoryDateTime({
        vesselId: this.vesselId,
        startTimeUtc: `${startTimeUtc}.000Z`,
        endTimeUtc: `${endTimeUtc}.000Z`
      });
    },

    timeToSliderValue(time) {
      let splitTime = time.split(":");
      let hh = Number(splitTime[0]) * 2;
      let mm = Number(splitTime[1]);
      if (mm === 30) {
        hh = hh + 1;
      }
      let start = hh;
      return [start, start + 1];
    },

    timeToSliderValue10min(time) {
      let sliderValue;
      Object.keys(this.sliderMarks).some(v => {
        if (this.sliderMarks[v].time === time) {
          sliderValue = this.sliderMarks[v].sliderValue;
          return true;
        }
      });
      return sliderValue;
    },

    sliderValueToTime(value) {
      let hh = value;
      let mm = "";
      if (hh % 2 !== 0) {
        mm = "30";
        hh = ("0" + String((value - 1) / 2)).slice(-2);
      } else {
        mm = "00";
        hh = ("0" + String(value / 2)).slice(-2);
      }
      return `${hh}:${mm}`;
    },

    sliderValueToTime10min(value) {
      let time = this.sliderMarks[value].time;
      return time;
    },

    toolTipFormat() {
      let start = this.sliderValueToTime10min(this.sliderValue[0]);
      let end = this.sliderValueToTime10min(this.sliderValue[1]);
      return `${start} ~ ${end}`;
    },

    adjustDom() {
      let sliderEl = document.getElementById("timeline-slider");
      let dotEl = sliderEl.getElementsByClassName("vue-slider-dot");
      dotEl[1].childNodes[0].remove("custom-dot");
      let tooltipEl = sliderEl.getElementsByClassName("vue-slider-dot-tooltip");
      tooltipEl[1].childNodes[0].remove("custom-dot");
      this.resizeCustomDotEl();
    },

    adjustSliderMarksDom() {
      let sliderEl = document.getElementById("timeline-slider");
      let sliderMarksEl = sliderEl.getElementsByClassName("vue-slider-marks");
      sliderMarksEl[0].childNodes.forEach( (n, i) => {
        if (i % 6 !== 0) {
          n.childNodes[0].style.height = "14px";
          n.childNodes[0].style.top =  "-10px";
        }
      })
    },

    resizeCustomDotEl() {
      const sliderEl = document.getElementById("timeline-slider");
      const processEl = sliderEl ? sliderEl.getElementsByClassName("vue-slider-process") : null;
      const customDotEl = sliderEl.getElementsByClassName("custom-dot");
      if(sliderEl && processEl) {
        customDotEl[0].style.width = `${processEl[0].clientWidth + 2}px`;
      }
    }
  },
  created(){
    this.crateSliderMarks10min();
  },
  mounted() {
    let roundedStartTimeLocal = DateUtil.toMomentObj(DateUtil.rounding10min(this.startTimeLocal)).local().format(
      "HH:mm"
    );
    this.sliderValue = this.timeToSliderValue10min(roundedStartTimeLocal);
    this.preSliderValue = this.timeToSliderValue10min(roundedStartTimeLocal);
    let self = this;
    window.onresize = function() {
      self.resizeCustomDotEl();
    };
    this.adjustDom();
    this.adjustSliderMarksDom();

    // ページ更新時左サイドバーの開閉が完了してからCustomDotのサイズを調整
    setTimeout(() => {
      self.resizeCustomDotEl();
    }, 2000);
  },
  beforeDestroy() {
    window.onresize = null;
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

#vessel-position-history-timeline-controller {
  .vue-slider-process {
    border-radius: 0px;
  }

  .vue-slider-mark-label {
    top: -55px;
  }

  .vue-slider-marks {
    background-color: #e8e8e8;
  }

  .vue-slider-mark-step {
    height: 24px;
    width: 1px;
    border: 1px solid #e8e8e8;
    border-radius: 0px;
    box-shadow: 0 0 0 0px #e8e8e8;
    background-color: #e8e8e8;
    position: absolute;
    top: -20px;
  }

  .custom-mark {
    position: absolute;
    top: -20px;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  .custom-dot {
    height: 62px;
    width: 100%;
    cursor: pointer;
    background: rgba(45, 156, 219, 0.5);
    border: 2px solid #2d9cdb;
    position: absolute;
    left: 5px;
    bottom: 4px;
    -webkit-transition: border-color 0.3s, -webkit-box-shadow 0.3s;
  }

  #timeline-mode-label {
    margin-left: 15px;
    li {
      text-align: center;
      float: left;
      line-height: 50px;
      .live-mode-text {
        margin-right: 10px;
      }
      .history-mode-text {
        margin-left: 10px;
        margin-right: 5px;
      }
      .history-mode-calendar {
        color: #3386dd;
      }
    }
  }

  p {
    position: relative;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    cursor: pointer;
  }

  p.active {
    position: relative;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    cursor: pointer;
    color: #3386dd;
  }

  p.active:before {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -5px;
    display: inline-block;
    width: 40px;
    height: 1px;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    background-color: #3386dd;
    border-radius: 2px;
  }

  #timeline-slider {
    width: calc(100% - 100px) !important;
    position: absolute;
    bottom: 30px;
  }
  .time-label {
    font-weight: 500;
    font-size: 14px;
    margin-left: 10px;
  }

  .timeline-control-button {
    color: $aisea-blue;
    border: none;

    &:active {
      opacity: 0.6;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}
</style>
